<template>
  <div v-if="show" class="picker-overlay" @click.self="close">
    <div class="picker-panel">
      <!-- Header -->
      <div class="picker-header">
        <h3><i class="bi bi-easel2"></i> Select Board</h3>
        <button class="close-btn" @click="close"><i class="bi bi-x-lg"></i></button>
      </div>

      <!-- Tabs: Library / Create New -->
      <div class="picker-tabs">
        <button class="picker-tab" :class="{ active: activeTab === 'library' }" @click="activeTab = 'library'">
          <i class="bi bi-grid-3x3-gap"></i> From Library
        </button>
        <button class="picker-tab" :class="{ active: activeTab === 'create' }" @click="activeTab = 'create'">
          <i class="bi bi-plus-lg"></i> Create New
        </button>
      </div>

      <!-- Library Tab -->
      <div v-if="activeTab === 'library'" class="picker-body">
        <!-- Search & filter -->
        <div class="picker-filters">
          <div class="search-box">
            <i class="bi bi-search"></i>
            <input type="text" v-model="searchQuery" placeholder="Search boards..." />
          </div>
          <div class="category-tabs">
            <button v-for="cat in categories" :key="cat.id" class="cat-tab"
              :class="{ active: activeCategory === cat.id }" @click="activeCategory = cat.id">
              {{ cat.label }}
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="picker-loading">
          <div class="spinner-border spinner-border-sm" role="status"></div>
          Loading...
        </div>

        <!-- Empty -->
        <div v-else-if="filteredBoards.length === 0" class="picker-empty">
          <p>No boards found. Try creating a new one!</p>
        </div>

        <!-- Board grid -->
        <div v-else class="picker-grid">
          <div v-for="board in filteredBoards" :key="board.id" class="picker-card"
            :class="{ selected: selectedIds.includes(board.id), disabled: alreadyLinkedIds.includes(board.id), landscape: isLandscape(board) }"
            @click="toggleSelect(board)">
            <div class="picker-card-preview" :class="{ 'landscape-preview': isLandscape(board) }">
              <svg :viewBox="`0 0 ${getBoardWidth(board)} ${getBoardHeight(board)}`" class="mini-canvas"
                preserveAspectRatio="xMidYMid meet">
                <rect width="100%" height="100%"
                  :fill="board.background === 'full-pitch' || board.background === 'full-pitch-landscape' || board.background === 'half-pitch' || board.background === 'pitch-two' || board.background === 'pitch-three' ? '#2d8a3e' : '#1e293b'" />
                <defs>
                  <template v-for="(el, idx) in (board.elements || []).slice(0, 30)">
                    <marker v-if="el.type === 'arrow'" :id="'pah-' + board.id + '-' + idx" :key="'mah-' + idx"
                      markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
                      <polygon points="0 0, 10 4, 0 8" :fill="el.color" />
                    </marker>
                    <marker v-if="el.type === 'curved-arrow'" :id="'pcah-' + board.id + '-' + idx" :key="'mcah-' + idx"
                      markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
                      <polygon points="0 0, 10 4, 0 8" :fill="el.color" />
                    </marker>
                  </template>
                </defs>
                <template v-for="(el, idx) in (board.elements || []).slice(0, 30)" :key="idx">
                  <!-- Arrow with arrowhead -->
                  <line v-if="el.type === 'arrow'" :x1="el.x1" :y1="el.y1" :x2="el.x2" :y2="el.y2" :stroke="el.color"
                    :stroke-width="el.strokeWidth" :marker-end="`url(#pah-${board.id}-${idx})`"
                    :transform="miniTransform(el)" />
                  <!-- Curved arrow -->
                  <path v-else-if="el.type === 'curved-arrow'"
                    :d="`M ${el.x1} ${el.y1} Q ${el.cpx} ${el.cpy} ${el.x2} ${el.y2}`" fill="none" :stroke="el.color"
                    :stroke-width="el.strokeWidth" :marker-end="`url(#pcah-${board.id}-${idx})`"
                    :transform="miniTransform(el)" />
                  <!-- Solid line -->
                  <line v-else-if="el.type === 'line'" :x1="el.x1" :y1="el.y1" :x2="el.x2" :y2="el.y2"
                    :stroke="el.color" :stroke-width="el.strokeWidth" />
                  <!-- Dashed line -->
                  <line v-else-if="el.type === 'dashed'" :x1="el.x1" :y1="el.y1" :x2="el.x2" :y2="el.y2"
                    :stroke="el.color" :stroke-width="el.strokeWidth" stroke-dasharray="10 6" />
                  <!-- Player marker (circle + label) -->
                  <g v-else-if="el.type === 'player'" :transform="miniTransform(el)">
                    <circle :cx="el.cx" :cy="el.cy" r="16" :fill="el.color" stroke="#fff" stroke-width="2" />
                    <text v-if="el.label" :x="el.cx" :y="el.cy + 5" text-anchor="middle" fill="#fff" font-size="13"
                      font-weight="700">{{ el.label }}</text>
                  </g>
                  <!-- Filled circle -->
                  <circle v-else-if="el.type === 'circle-filled'" :cx="el.cx" :cy="el.cy" :r="el.r || 16"
                    :fill="el.color" />
                  <!-- Empty circle -->
                  <circle v-else-if="el.type === 'circle-empty'" :cx="el.cx" :cy="el.cy" :r="el.r" fill="none"
                    :stroke="el.color" :stroke-width="el.strokeWidth" />
                  <!-- Zone box -->
                  <rect v-else-if="el.type === 'zone'" :x="Math.min(el.x1, el.x2)" :y="Math.min(el.y1, el.y2)"
                    :width="Math.abs(el.x2 - el.x1)" :height="Math.abs(el.y2 - el.y1)" :fill="el.color + '33'"
                    :stroke="el.color" :stroke-width="el.strokeWidth" stroke-dasharray="6 3" rx="4"
                    :transform="miniTransform(el)" />
                  <!-- Text label -->
                  <text v-else-if="el.type === 'text'" :x="el.cx" :y="el.cy" :fill="el.color"
                    :font-size="el.fontSize || 16" font-weight="600" dominant-baseline="central">{{ el.text }}</text>
                  <!-- Cone -->
                  <g v-else-if="el.type === 'cone'" :transform="miniTransform(el)">
                    <polygon :points="`${el.cx},${el.cy - 12} ${el.cx - 9},${el.cy + 6} ${el.cx + 9},${el.cy + 6}`"
                      :fill="el.color" opacity="0.85" />
                    <line :x1="el.cx - 10" :y1="el.cy + 6" :x2="el.cx + 10" :y2="el.cy + 6" :stroke="el.color"
                      stroke-width="2.5" stroke-linecap="round" />
                  </g>
                  <!-- Hurdle -->
                  <g v-else-if="el.type === 'hurdle'" :transform="miniTransform(el)">
                    <line :x1="el.cx - 12" :y1="el.cy - 6" :x2="el.cx + 12" :y2="el.cy - 6" :stroke="el.color"
                      stroke-width="2.5" />
                    <line :x1="el.cx - 10" :y1="el.cy - 6" :x2="el.cx - 10" :y2="el.cy + 6" :stroke="el.color"
                      stroke-width="2" />
                    <line :x1="el.cx + 10" :y1="el.cy - 6" :x2="el.cx + 10" :y2="el.cy + 6" :stroke="el.color"
                      stroke-width="2" />
                  </g>
                  <!-- Disk -->
                  <ellipse v-else-if="el.type === 'disk'" :cx="el.cx" :cy="el.cy" rx="10" ry="5" :fill="el.color" />
                  <!-- Freehand path -->
                  <path v-else-if="el.type === 'freehand'" :d="el.d" fill="none" :stroke="el.color"
                    :stroke-width="el.strokeWidth" stroke-linecap="round" stroke-linejoin="round" />
                  <!-- Goal (simplified for thumbnail) -->
                  <g v-else-if="el.type === 'goal'" :transform="miniTransform(el)">
                    <rect :x="el.cx - (el.goalSize === 'small' ? 16 : el.goalSize === 'medium' ? 26 : 40)" :y="el.cy"
                      :width="(el.goalSize === 'small' ? 32 : el.goalSize === 'medium' ? 52 : 80)"
                      :height="(el.goalSize === 'small' ? 18 : el.goalSize === 'medium' ? 22 : 28)"
                      fill="rgba(255,255,255,0.1)" :stroke="el.color" stroke-width="2" />
                  </g>
                </template>
              </svg>
              <div v-if="alreadyLinkedIds.includes(board.id)" class="linked-badge">
                <i class="bi bi-link-45deg"></i> Linked
              </div>
            </div>
            <div class="picker-card-info">
              <span class="picker-card-title">{{ board.title }}</span>
            </div>
            <div v-if="selectedIds.includes(board.id)" class="check-overlay">
              <i class="bi bi-check-circle-fill"></i>
            </div>
          </div>
        </div>

        <!-- Confirm button -->
        <div v-if="selectedIds.length > 0" class="picker-confirm">
          <span class="selection-count">{{ selectedIds.length }} board{{ selectedIds.length > 1 ? 's' : '' }}
            selected</span>
          <button class="btn-confirm" @click="confirmSelection">
            <i class="bi bi-check-lg"></i> Attach Selected
          </button>
        </div>
      </div>

      <!-- Create New Tab -->
      <div v-if="activeTab === 'create'" class="picker-body">
        <div class="create-container">
          <TacticalBoard :board="newBoard" :showActions="false" :startInEditMode="true" @saved="onNewBoardSaved"
            @cancelled="activeTab = 'library'" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TacticalBoard from './index.vue';
import { TacticalBoardService } from '@/services/tactical-board-service';
import { mapGetters } from 'vuex';

export default {
  name: 'TacticalBoardPicker',
  components: { TacticalBoard },
  props: {
    show: { type: Boolean, default: false },
    alreadyLinkedIds: { type: Array, default: () => [] },
    defaultCategory: { type: String, default: null },
  },
  emits: ['close', 'boards-selected'],
  data() {
    return {
      boards: [],
      loading: false,
      searchQuery: '',
      activeCategory: 'all',
      activeTab: 'library',
      selectedIds: [],
      newBoard: {
        id: null,
        title: 'Tactical Board',
        description: '',
        category: 'general',
        elements: [],
        notes: '',
        background: 'full-pitch',
        background_image: '',
        orientation: 'portrait',
        image_transform: { x: 0, y: 0, scale: 1 },
      },
      categories: [
        { id: 'all', label: 'All' },
        { id: 'training', label: 'Training' },
        { id: 'rtp', label: 'RTP' },
        { id: 'set-piece', label: 'Set Pieces' },
        { id: 'formation', label: 'Formations' },
        { id: 'general', label: 'General' },
      ],
    };
  },
  computed: {
    ...mapGetters(['getProfile']),
    filteredBoards() {
      let filtered = this.boards;

      if (this.activeCategory !== 'all') {
        filtered = filtered.filter(b => b.category === this.activeCategory);
      }

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(b =>
          (b.title || '').toLowerCase().includes(q) ||
          (b.description || '').toLowerCase().includes(q)
        );
      }

      return filtered;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.selectedIds = [];
        this.activeTab = 'library';
        if (this.defaultCategory) {
          this.activeCategory = this.defaultCategory;
          this.newBoard.category = this.defaultCategory;
        }
        this.loadBoards();
      }
    },
  },
  methods: {
    async loadBoards() {
      this.loading = true;
      try {
        const profile = this.getProfile || {};
        const res = await TacticalBoardService.getTacticalBoards({ clubId: profile.clubId });
        if (res.data.success) {
          this.boards = res.data.data || [];
        }
      } catch (err) {
        console.error('Error loading boards:', err);
      } finally {
        this.loading = false;
      }
    },

    toggleSelect(board) {
      if (this.alreadyLinkedIds.includes(board.id)) return;

      const idx = this.selectedIds.indexOf(board.id);
      if (idx >= 0) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(board.id);
      }
    },

    confirmSelection() {
      const selected = this.boards.filter(b => this.selectedIds.includes(b.id));
      this.$emit('boards-selected', selected);
      this.close();
    },

    onNewBoardSaved(savedBoard) {
      // Auto-select the newly created board and emit
      this.$emit('boards-selected', [savedBoard]);
      this.close();
    },

    close() {
      this.selectedIds = [];
      this.$emit('close');
    },

    /** Return the correct canvas width for the board's background, matching TacticalBoardCanvas cW/cH */
    getBoardWidth(board) {
      const bg = board.background || 'full-pitch';
      const map = {
        'full-pitch': 500,
        'full-pitch-landscape': 700,
        'half-pitch': 396,
        'pitch-two': 700,
        'pitch-three': 700,
        'blank': 500,
      };
      if (map[bg] !== undefined) return map[bg];
      // fallback based on orientation
      return board.orientation === 'landscape' ? 700 : 500;
    },

    getBoardHeight(board) {
      const bg = board.background || 'full-pitch';
      const map = {
        'full-pitch': 700,
        'full-pitch-landscape': 500,
        'half-pitch': 500,
        'pitch-two': 500,
        'pitch-three': 500,
        'blank': 700,
      };
      if (map[bg] !== undefined) return map[bg];
      return board.orientation === 'landscape' ? 500 : 700;
    },

    isLandscape(board) {
      const w = this.getBoardWidth(board);
      const h = this.getBoardHeight(board);
      return w > h;
    },

    /** Compute transform string for element rotation/scale (mirrors TacticalBoardCanvas.elementTransform) */
    miniTransform(el) {
      const rot = el.rotation || 0;
      const s = el.scale || 1;
      let cx, cy;
      if (el.cx !== undefined) { cx = el.cx; cy = el.cy; }
      else if (el.x1 !== undefined) { cx = (el.x1 + el.x2) / 2; cy = (el.y1 + el.y2) / 2; }
      else return '';
      if (s === 1 && rot === 0) return '';
      if (s === 1) return `rotate(${rot}, ${cx}, ${cy})`;
      return `translate(${cx}, ${cy}) scale(${s}) rotate(${rot}) translate(${-cx}, ${-cy})`;
    },
  },
};
</script>

<style scoped>
.picker-overlay {
  position: fixed;
  inset: 0;
  background: #0f172a;
  z-index: 1050;
  display: flex;
}

.picker-panel {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ---- Header ---- */
.picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.picker-header h3 {
  font-size: 1rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.picker-header h3 i {
  color: #667eea;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
}

/* ---- Tabs ---- */
.picker-tabs {
  display: flex;
  gap: 4px;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.picker-tab {
  padding: 8px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}

.picker-tab:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
}

.picker-tab.active {
  background: rgba(102, 126, 234, 0.2);
  border-color: #667eea;
  color: #667eea;
}

/* ---- Body ---- */
.picker-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

/* ---- Filters ---- */
.picker-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 6px 12px;
}

.search-box i {
  color: #64748b;
  font-size: 0.8rem;
}

.search-box input {
  background: none;
  border: none;
  color: #e2e8f0;
  font-size: 0.8rem;
  outline: none;
  width: 140px;
}

.search-box input::placeholder {
  color: #475569;
}

.category-tabs {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.cat-tab {
  padding: 4px 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
  color: #94a3b8;
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.cat-tab:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
}

.cat-tab.active {
  background: rgba(102, 126, 234, 0.2);
  border-color: #667eea;
  color: #667eea;
}

/* ---- Loading / Empty ---- */
.picker-loading,
.picker-empty {
  text-align: center;
  padding: 30px;
  color: #64748b;
  font-size: 0.85rem;
}

.picker-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

/* ---- Grid ---- */
.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.picker-card {
  position: relative;
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
}

.picker-card:hover:not(.disabled) {
  border-color: rgba(102, 126, 234, 0.4);
}

.picker-card.selected {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
}

.picker-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.picker-card-preview {
  position: relative;
  aspect-ratio: 5 / 7;
  overflow: hidden;
  background: #1a1a2e;
}

.picker-card-preview.landscape-preview {
  aspect-ratio: 7 / 5;
}

.mini-canvas {
  width: 100%;
  height: 100%;
}

.linked-badge {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(34, 197, 94, 0.8);
  color: white;
  font-size: 0.6rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 3px;
}

.picker-card-info {
  padding: 8px 10px;
}

.picker-card-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.check-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 1.2rem;
  color: #667eea;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
}

/* ---- Confirm bar ---- */
.picker-confirm {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 16px;
}

.selection-count {
  font-size: 0.8rem;
  color: #94a3b8;
}

.btn-confirm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: rgba(34, 197, 94, 0.2);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #4ade80;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-confirm:hover {
  background: rgba(34, 197, 94, 0.3);
}

/* ---- Create container ---- */
.create-container {
  min-height: 500px;
}
</style>
